import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import type { ProviderEditorFormData } from '../schemas';

type AdditionalDetailsSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
};

type FormFieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  hint?: string;
};

function FormField({
  label,
  required = false,
  error,
  htmlFor,
  children,
  hint,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/**
 * AdditionalDetailsSection - Miscellaneous provider information
 * 
 * Contains:
 * - Media type (for hero media)
 * 
 * Note: Referral code and statistics (consultation count, monthly consultations)
 * are read-only fields managed by the system and not part of this form.
 * They should be displayed separately if needed for informational purposes.
 */
export function AdditionalDetailsSection({
  control,
  errors,
}: AdditionalDetailsSectionProps) {
  return (
    <div className="space-y-6">
      {/* Media Type */}
      <FormField
        label="Hero Media Type"
        htmlFor="media-type"
        error={errors.provider?.mediaType?.message}
        hint="Type of hero media (image, video, etc.) - max 10 characters"
      >
        <Controller
          name="provider.mediaType"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="media-type"
              maxLength={10}
              className={errors.provider?.mediaType ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Informational Note */}
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Additional provider information such as referral codes and 
          consultation statistics are automatically managed by the system and displayed 
          in read-only sections elsewhere in the admin portal.
        </p>
      </div>
    </div>
  );
}

